import { Multiselect, Select } from '@cloudscape-design/components';
import React, { useEffect, useState } from 'react';
import { ItemCharges, OptionDefinition } from '../../../src/resources/types';
import { useGlobalState } from '../../pages/context/GlobalStateContext';

// When single = true
interface SelectChargeSingleProps {
    single: true;
    visible?: boolean;
    value?: number; // Single value: number (idCharge)
    readonly?: boolean;
    width?: number;
    tokenLimit?: number;
    hideTokens?: boolean;
    onSelect?: (selectedCharge: number | undefined) => void;
}

// When single = false
interface SelectChargeMultiProps {
    single?: false; // default is "false"
    visible?: boolean;
    value?: number[]; // Multi value: number[] (idCharge list)
    readonly?: boolean;
    width?: number;
    tokenLimit?: number;
    hideTokens?: boolean;
    onSelect?: (selectedCharges: number[]) => void;
}

export type SelectChargeProps =
    | SelectChargeSingleProps
    | SelectChargeMultiProps;

// -----------------------------
// COMPONENT IMPLEMENTATION
// -----------------------------

const SelectCharges: React.FC<SelectChargeProps> = (props) => {
    const { single, value, readonly, onSelect, width = 200, tokenLimit = 3, hideTokens = false } = props;
    const { data } = useGlobalState();

    // Utility array that maps raw data to the full ItemCharges structure
    const mappedCharges: ItemCharges[] = data.charges.map((plan: ItemCharges) => ({
        idCharge: plan.idCharge,
        charge: plan.charge,
        currency: plan.currency,
        ammount: plan.ammount,
        accountPlan: plan.accountPlan,
        value: plan.idCharge.toString(),
        label: plan.charge,
    }));

    // 💡 FIX: Create a separate array that is strictly OptionDefinition[] 
    // This resolves the error on the `options` prop.
    const chargesOptions: OptionDefinition[] = mappedCharges.map(charge => ({
        label: charge.label, // Use the label property already set on ItemCharges
        value: charge.value, // Use the value property already set on ItemCharges
    }));

    // State for Single Select: stores a simplified OptionDefinition object
    const [selectedSingle, setSelectedSingle] = useState<OptionDefinition | undefined>(
        single
            ? (() => {
                const match = mappedCharges.find(c => c.idCharge === value);
                return match ? { label: match.charge, value: String(match.idCharge) } : undefined;
            })()
            : undefined
    );

    // State for Multi Select: stores OptionDefinition[]
    const [selectedMulti, setSelectedMulti] = useState<OptionDefinition[]>(
        !single && Array.isArray(value)
            ? value
                .map(v => {
                    const match = mappedCharges.find((c: ItemCharges) => c.idCharge === v);
                    if (!match) return null;
                    return {
                        label: match.charge,
                        value: String(match.idCharge)
                    } satisfies OptionDefinition;
                })
                .filter((x): x is OptionDefinition => x !== null)
            : []
    );

    // Update internal state when the external 'value' prop changes
    useEffect(() => {
        if (single) {
            const match = mappedCharges.find(c => c.idCharge === value);
            if (match) {
                setSelectedSingle({ label: match.charge, value: String(match.idCharge) });
            } else {
                setSelectedSingle(undefined);
            }
        } else if (Array.isArray(value)) {
            const multiOptions = value
                .map(v => {
                    const match = mappedCharges.find((c: ItemCharges) => c.idCharge === v);
                    if (!match) return null;
                    return { label: match.charge, value: String(match.idCharge) } as OptionDefinition;
                })
                .filter((x): x is OptionDefinition => x !== null);
            setSelectedMulti(multiOptions);
        }
    }, [value, single, data.charges]);

    // Helper to find the ItemCharges object from ID
    const findChargeById = (id: string | number): ItemCharges | undefined => {
        const idNumber = typeof id === 'string' ? Number(id) : id;
        return mappedCharges.find((item: ItemCharges) => item.idCharge === idNumber);
    };

    const placeholder = single ? "Seleccionar cargo" : selectedMulti && selectedMulti.length > 0 ? `${selectedMulti.length} seleccionado${selectedMulti.length > 1 ? 's' : ''}` : "Seleccionar cargos";

    return (
        <div style={{ width: `${width}px` }}>
            {/* Single Select Mode */}
            {single && (
                <Select
                    placeholder={placeholder}
                    readOnly={readonly}
                    selectedOption={selectedSingle ?? null}
                    filteringType="auto"
                    // ✅ FIX APPLIED HERE: Use the strictly mapped array
                    options={chargesOptions}
                    onChange={({ detail }) => {
                        const idString = detail.selectedOption?.value;
                        const fullCharge = findChargeById(idString ?? "");

                        if (fullCharge) {
                            // Update internal state (OptionDefinition)
                            setSelectedSingle({ label: fullCharge.label, value: fullCharge.value });

                            // Call external handler (ItemCharges)
                            const singleProps = props as SelectChargeSingleProps;
                            if (singleProps.onSelect) {
                                singleProps.onSelect(fullCharge.idCharge);
                            }
                        } else {
                            setSelectedSingle(undefined);
                            const singleProps = props as SelectChargeSingleProps;
                            if (singleProps.onSelect) {
                                singleProps.onSelect(undefined);
                            }
                        }
                    }}
                />
            )}

            {/* Multi Select Mode */}
            {!single && (
                <Multiselect
                    placeholder={placeholder}
                    hideTokens={hideTokens}
                    tokenLimit={tokenLimit}
                    readOnly={readonly}
                    selectedOptions={selectedMulti}
                    filteringType="auto"
                    // ✅ FIX APPLIED HERE: Use the strictly mapped array
                    options={chargesOptions}
                    onChange={({ detail }) => {
                        const list = detail.selectedOptions as OptionDefinition[];

                        setSelectedMulti([...list]);

                        const multiProps = props as SelectChargeMultiProps;

                        if (multiProps.onSelect) {
                            // ✅ CHANGED: Map directly to the 'value' property (idCharge)
                            // Assuming o.value is your idCharge
                            const selectedIds = list.map(o => Number(o.value));

                            // Pass the array of IDs to your parent component
                            multiProps.onSelect(selectedIds);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default SelectCharges;